import React, { createContext, useContext } from 'react';
import type { PortalLoaderData } from '../lib/portal-loaders';

// Type definition for portal context stored on window
interface PortalContext {
  organizationInfo?: {
    organizationId: string;
    organizationSlug: string;
    organizationName: string;
  };
}

// Extend window interface to include portal context
declare global {
  interface Window {
    __PORTAL_CONTEXT__?: PortalContext;
  }
}

const PortalContext = createContext<PortalLoaderData | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const usePortalContext = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortalContext must be used within a PortalProvider');
  }
  return context;
};

interface PortalProviderProps {
  children: React.ReactNode;
  value: PortalLoaderData;
}

export const PortalProvider: React.FC<PortalProviderProps> = ({ children, value }) => {
  // Set global portal context for API service access
  React.useEffect(() => {
    // Only set the context if it's not already set by the loader
    // The loader sets it earlier to ensure AuthContext can access it during initialization
    if (!window.__PORTAL_CONTEXT__) {
      window.__PORTAL_CONTEXT__ = {
        organizationInfo: {
          organizationId: value.organizationInfo.organizationId,
          organizationSlug: value.organizationInfo.subdomain,
          organizationName: value.organizationInfo.name
        }
      };
    }
    
    return () => {
      delete window.__PORTAL_CONTEXT__;
    };
  }, [value]);

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
};